import type { RangeValue } from "@react-types/shared";
import type { Metric, MetricsResponse } from "@unbox/api";
import { useMemo } from "react";
import type { DateValue } from "react-aria";
import { useMetrics } from "@/api/metrics";
import type { TimePeriod } from "../types";
import { formatDateByPeriod } from "../utils";

const transformApiDataToChart = (apiData: MetricsResponse | null | undefined, timePeriod: TimePeriod) => {
  if (!apiData) return [];

  // Check if apiData has any values
  const metricArrays = Object.values(apiData);
  if (metricArrays.length === 0 || metricArrays.every((arr) => !arr || arr.length === 0)) return [];

  // Step 1: Collect all unique dates across all metrics
  const allDates = new Set<string>();
  for (const metricArray of metricArrays) {
    if (metricArray) {
      for (const dataPoint of metricArray) {
        if (dataPoint?.periodStart) {
          allDates.add(dataPoint.periodStart);
        }
      }
    }
  }

  // Step 2: Create a map of date -> metric values
  const dateToMetrics = new Map<string, Record<string, number | null>>();

  for (const [metricKey, metricArray] of Object.entries(apiData)) {
    if (!metricArray) continue;

    for (const dataPoint of metricArray) {
      if (!dataPoint?.periodStart) continue;

      const date = dataPoint.periodStart;
      if (!dateToMetrics.has(date)) {
        dateToMetrics.set(date, {});
      }
      dateToMetrics.get(date)![metricKey] = dataPoint.value ?? null;
    }
  }

  // Step 3: Build chart data from the date map
  const chartData = Array.from(allDates).map((periodStart) => {
    const date = new Date(periodStart);
    const dateLabel = formatDateByPeriod(date, timePeriod);
    const metricValues = dateToMetrics.get(periodStart) || {};

    return {
      date: periodStart, // ISO date string for TimeSeriesDataPoint
      periodStart: date, // Raw date for logic (filtering, sorting)
      dateLabel: dateLabel, // Formatted string for display
      ...metricValues,
    };
  });

  // Sort by date (oldest first)
  return chartData.sort((a, b) => a.periodStart.getTime() - b.periodStart.getTime());
};

// Custom hook for processed metrics data
export const useProcessedMetricsData = (
  metrics: Metric[],
  timePeriod: TimePeriod,
  dateRange: RangeValue<DateValue> | null,
  fillGaps: boolean = false, // New parameter: whether to fill missing dates with null values
) => {
  // Forward the selected range to the API so the server scopes the data (and returns
  // all-source GMV). The client-side filter below then acts as a safety net.
  const from = dateRange?.start ? dateRange.start.toString().split("T")[0] : undefined;
  const to = dateRange?.end ? dateRange.end.toString().split("T")[0] : undefined;
  const { data: metricsData, isLoading: isLoadingMetrics } = useMetrics(metrics, timePeriod, from, to);

  const rawData = useMemo(() => transformApiDataToChart(metricsData, timePeriod), [metricsData, timePeriod]);

  const filteredData = useMemo(() => {
    if (!dateRange) {
      // Show last month of data when no date range is set
      if (rawData.length === 0) return [];

      // Sort data by date (newest first) - use periodStart directly
      const sortedData = [...rawData].sort((a, b) => {
        return b.periodStart.getTime() - a.periodStart.getTime();
      });

      // Return the last month of data (approximately 4-5 data points)
      const lastMonthData = sortedData.slice(0, Math.min(5, sortedData.length));
      return lastMonthData.reverse(); // Return in chronological order
    }

    // Filter data based on selected date range
    const fromDate = dateRange.start.toDate("UTC");
    const toDate = dateRange.end.toDate("UTC");

    // If not filling gaps, just filter existing data
    if (!fillGaps) {
      const filtered = rawData.filter((dataPoint) => {
        return dataPoint.periodStart >= fromDate && dataPoint.periodStart <= toDate;
      });

      // Sort filtered data chronologically (oldest first)
      return filtered.sort((a, b) => {
        return a.periodStart.getTime() - b.periodStart.getTime();
      });
    }

    // Fill gaps: Just return filtered data without filling
    // The gap filling creates too many data points for weekly/monthly periods
    // Instead, we just filter to show only the data points that exist within the range
    const filtered = rawData.filter((dataPoint) => {
      return dataPoint.periodStart >= fromDate && dataPoint.periodStart <= toDate;
    });

    // Sort filtered data chronologically (oldest first)
    return filtered.sort((a, b) => {
      return a.periodStart.getTime() - b.periodStart.getTime();
    });
  }, [rawData, dateRange, timePeriod, fillGaps]);

  return {
    rawData,
    filteredData,
    isLoadingMetrics,
  };
}; // Chart data transformation similar to ChartBuilder
